import React, { useEffect, useState } from 'react'
import { Box, Flex, Text } from '@chilipiper/design-system'
import momentTz, { Moment } from 'moment-timezone'
import { BookerCalendarEvent } from '@chilipiper/api-type-def'
import { useTranslations, T, useLocale } from '@chilipiper/locale'
import { TimezoneSelect, Icon, ScreenReaderOnly } from '@chilipiper/design-system'
import { SvgArrowTriangleDown } from '@chilipiper/icons/src/arrow/ArrowTriangleDown'
import { Times, Event as EventType } from '../times/Times'
import { IntercomData } from '../types'
import { NoAvailableTimeSlots } from './NoAvailableTimeSlots'
import { useComputeEventOfDay } from './hooks'
import { trackSlotAvailability } from './helpers'

interface Props {
  date: Moment
  events: BookerCalendarEvent[]
  getDuration?: () => number
  intercomData?: IntercomData | null
  noMemberFound: boolean
  onTimeChange: (time: Moment, event: EventType) => void
  onZoneChange: (timezone: string) => void
  time?: number
  timeLoading: boolean
  timezone: string
}

export const TimeSection = ({
  getDuration,
  onZoneChange,
  onTimeChange,
  timezone,
  intercomData,
  noMemberFound,
  timeLoading,
  date,
  time,
  events,
}: Props) => {
  const locale = useLocale()
  const [
    meetingLength,
    timeBookHeader,
    noAvailableTimeSlotsOnThisDay,
    noAvailableTimeSlots,
    tryAnotherDay,
  ] = useTranslations([
    'meetingLength',
    'timeBookHeader',
    'noAvailableTimeSlotsOnThisDay',
    'noAvailableTimeSlots',
    'tryAnotherDay',
  ])

  const [renderNoTimesOnThisDay, setRenderNoTimesOnThisDay] = useState(false)
  const overwriteTimezone = intercomData?.timezone ?? false
  const eventOfDay = useComputeEventOfDay({ date, events })
  const renderNoTimes = !timeLoading && noMemberFound
  const renderTimeSlots = !renderNoTimesOnThisDay && !renderNoTimes
  const timezoneTitle = `${timezone} Time (${momentTz(time).tz(timezone).format('LT')})`

  const onSlotClick = (start: moment.Moment, event: EventType) => {
    onTimeChange(start, event)
    window.parent?.postMessage(
      {
        action: 'timeslot-selected',
        args: { time: start.toDate(), timezone, duration: getDuration?.() ?? 0 },
      },
      '*'
    )
  }

  useEffect(() => {
    setRenderNoTimesOnThisDay(!timeLoading && !noMemberFound && !eventOfDay.length)
  }, [eventOfDay.length, timeLoading, noMemberFound])
  useEffect(() => {
    if (!renderTimeSlots) {
      trackSlotAvailability(eventOfDay.length ?? 0)
    }
  }, [date, eventOfDay.length, renderTimeSlots, timeLoading])
  useEffect(() => {
    if (!timeLoading) {
      window.parent?.postMessage({ action: 'availability-loaded' }, '*')
    }
  }, [timeLoading, date])

  return (
    <div className='fabe-times'>
      <NoAvailableTimeSlots renderNoTimes={renderNoTimes} title={noAvailableTimeSlots} />
      <NoAvailableTimeSlots
        renderNoTimes={renderNoTimesOnThisDay}
        title={noAvailableTimeSlotsOnThisDay}
        subtitle={tryAnotherDay}
      />
      {renderTimeSlots && (
        <>
          <Flex
            w='100%'
            maxWidth='480px'
            justifyContent='center'
            alignItems={['start', 'center']}
            px={[6, 0]}
            flexDirection='column'
          >
            <Flex flex={1} justifyContent={['start', 'center']}>
              <Text
                textStyle='heading-m'
                data-chili-css='times-header'
                color='neutral-dark-100'
                as='h2'
                marginY={0}
              >
                {timeBookHeader}
              </Text>
            </Flex>
            <span className='fabe-times__caption'>
              {getDuration && (
                <>
                  <small data-chili-css='times-duration'>
                    <T data={{ duration: timeLoading ? 'Loading' : getDuration().toString() }}>
                      {meetingLength}
                    </T>
                  </small>
                  <span
                    data-chili-css='times-separator'
                    className='fabe-times__caption-separator'
                  />
                </>
              )}
              <div className='fabe-timezone-header'>
                {!timeLoading && (
                  <ScreenReaderOnly role='status'>
                    Time slots loaded. Current timezone is {overwriteTimezone || timezone}. Press
                    control + D to change.
                  </ScreenReaderOnly>
                )}

                <TimezoneSelect
                  optionsStyle={{ sameWidth: false }}
                  maxWidth={356}
                  value={overwriteTimezone || timezone}
                  onChange={onZoneChange}
                  locale={locale}
                  customPopoverTrigger={
                    <div data-test-id='timezone-chooser' className='fabe-timezone-chooser-outer'>
                      <ScreenReaderOnly>
                        Current timezone is {overwriteTimezone || timezone}. Press enter to change.
                      </ScreenReaderOnly>
                      <Flex
                        fontWeight={500}
                        fontSize={14}
                        color='neutral-dark-64'
                        data-chili-css='timezone-chooser'
                        className='fabe-timezone-chooser-inner'
                        aria-hidden
                      >
                        <div>{timezoneTitle}</div>
                        <Box w={3} color='neutral-dark-32'>
                          <Icon
                            icon={SvgArrowTriangleDown}
                            alt={`Current timezone is ${
                              overwriteTimezone || timezone
                            }. Press enter to change.`}
                            pointer
                          />
                        </Box>
                      </Flex>
                      <div className='fabe-timezone-line' />
                    </div>
                  }
                />
              </div>
            </span>
          </Flex>
          <Times
            loading={timeLoading}
            onChange={onSlotClick}
            date={date}
            time={momentTz(time)}
            events={events}
          />
        </>
      )}
    </div>
  )
}
